<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onScopeDispose,
    ref,
    watch,
  } from 'vue';
  import { useEventListener } from '@vueuse/core';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { useReservationsSettings } from '@/composition/reservations/use-reservations-settings';
  import { registerReservationBranchLeave } from '@/composition/reservations/reservation-branch-leave';
  import { useSettingsSectionSpy } from '@/composition/reservations/use-settings-section-spy';
  import { useAuthStore } from '@/composition/use-auth';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import V2ReservationsGeneralSettings from './settings/V2ReservationsGeneralSettings.vue';
  import V2ReservationsShiftsSettings from './settings/V2ReservationsShiftsSettings.vue';
  import V2ReservationsDatesSettings from './settings/V2ReservationsDatesSettings.vue';
  import V2ReservationsTablesPlanSettings from './settings/V2ReservationsTablesPlanSettings.vue';
  import V2ReservationsDepositSettings from './settings/V2ReservationsDepositSettings.vue';
  import V2ReservationsMessagesSettings from './settings/V2ReservationsMessagesSettings.vue';
  import V2ReservationsLinkSettings from './settings/V2ReservationsLinkSettings.vue';
  import V2ReservationsSetupChecklist from './settings/V2ReservationsSetupChecklist.vue';
  import { getReservationSetupSteps } from '@/domain/reservations/reservation-setup';
  import { maxBookableOnlineParty } from '@/domain/reservations/reservation-capacity';

  const emit = defineEmits<{ (e: 'setup'): void }>();
  const sections = [
    'general',
    'shifts',
    'dates',
    'tables',
    'deposit',
    'messages',
    'link',
  ];
  const rootElement = ref<HTMLElement | null>(null);
  const scrollElement = ref<HTMLElement | null>(null);
  const navElement = ref<HTMLElement | null>(null);
  const linksElement = ref<HTMLElement | null>(null);
  const tableSettings = ref<{
    flush: () => Promise<boolean>;
    discard: () => void;
    hasPending: () => boolean;
  } | null>(null);
  const restaurant = useRestaurantStore();
  const auth = useAuthStore();
  const modals = useModal();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const {
    configuration,
    draft,
    status,
    saving,
    dirty,
    tableTimesDirty,
    load,
    save,
    discard,
  } = useReservationsSettings();
  const currency = computed(
    () => restaurant.currency?.symbol ?? restaurant.restaurant?.currency ?? '$'
  );
  const setupComplete = computed(() =>
    configuration.value
      ? getReservationSetupSteps(configuration.value).complete
      : false
  );
  const paymentsUrl = computed(
    () => `/${restaurant.restaurant?.stringId}/settings?tab=orders`
  );
  const maxBookableParty = computed(() =>
    configuration.value
      ? maxBookableOnlineParty(
          configuration.value.tables,
          configuration.value.tableGroups
        )
      : 0
  );
  /** "Lower the maximum" from the tables card edits the draft; saving stays explicit. */
  function lowerMaxParty(max: number) {
    if (!draft.value) return;
    draft.value = {
      ...draft.value,
      maxPartyOnline: max,
      minPartyOnline: Math.min(draft.value.minPartyOnline, max),
    };
    ui.showToast(
      AlertType.Info,
      String(proxy.$t('reservations.notice.lowerMaxDone', { max }))
    );
  }
  const invalidDeposit = computed(
    () =>
      !!draft.value &&
      draft.value.depositMode !== 'none' &&
      (!Number.isFinite(draft.value.depositAmountPerPerson) ||
        draft.value.depositAmountPerPerson <= 0)
  );
  const layoutReady = computed(() => !!configuration.value && !!draft.value);
  // The navigation follows the reader; below `lg` it is sticky and covers the top.
  const { active: activeSection, go } = useSettingsSectionSpy({
    container: scrollElement,
    sections,
    elementFor: (section) =>
      rootElement.value?.querySelector<HTMLElement>(
        `#rsv-settings-${section}`
      ) ?? null,
    topOffset: () =>
      window.innerWidth < 1024 ? navElement.value?.offsetHeight ?? 0 : 0,
    ready: layoutReady,
  });
  let disposed = false;
  let leaveConfirmation: Promise<boolean> | null = null;

  function t(key: string): string {
    return String(proxy.$t(key));
  }
  function selectSection(section: string) {
    go(section);
    rootElement.value
      ?.querySelector<HTMLElement>(`#rsv-settings-${section}`)
      ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }
  // Deep link (`?section=`) from the guide's finish screen: the page clears
  // the param when the tab changes, so it is read once the cards exist.
  watch(
    layoutReady,
    (ready) => {
      const section = proxy.$route.query.section;
      if (ready && typeof section === 'string' && sections.includes(section))
        void nextTick(() => selectSection(section));
    },
    { immediate: true }
  );
  // On narrow screens the chip row scrolls sideways: keep the active chip in view.
  watch(activeSection, () => {
    void nextTick(() => {
      const links = linksElement.value;
      const chip = links?.querySelector<HTMLElement>('.section-link.active');
      if (!links || !chip || links.scrollWidth <= links.clientWidth) return;
      links.scrollTo({
        left: Math.max(0, chip.offsetLeft - 16),
        behavior: 'smooth',
      });
    });
  });
  async function reload() {
    try {
      await load({ preserveDraft: true });
    } catch (error) {
      if (!disposed)
        ui.serverError(error, t('reservations.settings.loadError'));
    }
  }
  async function saveDraft() {
    const invalidField = Array.from(
      rootElement.value?.querySelectorAll<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >('input, select, textarea') ?? []
    ).find(
      (field) =>
        field.closest(
          '#rsv-settings-general, #rsv-settings-deposit, #rsv-settings-messages'
        ) &&
        !field.disabled &&
        !field.checkValidity()
    );
    if (invalidField) {
      invalidField.scrollIntoView({ block: 'center', behavior: 'smooth' });
      invalidField.reportValidity();
      return;
    }
    if (invalidDeposit.value) {
      selectSection('deposit');
      return;
    }
    try {
      const saved = await save();
      if (saved && !disposed)
        ui.showToast(AlertType.Success, t('reservations.settings.saved'));
    } catch (error) {
      if (!disposed)
        ui.serverError(error, t('reservations.settings.saveError'));
    }
  }
  function confirmLeave(): Promise<boolean> {
    if (!leaveConfirmation)
      leaveConfirmation = (async () => {
        if (!((await tableSettings.value?.flush()) ?? true)) {
          // The table write keeps failing: offer to drop it rather than trap
          // the owner on this tab.
          if (disposed) return false;
          const dropTables = await modals.confirm({
            title: t('floorPlan.leave.title'),
            message: t('reservations.settings.unsaved'),
            acceptText: t('reservations.settings.discard'),
            cancelText: t('cancel'),
            acceptColor: 'error',
          });
          if (disposed) return false;
          if (!dropTables) {
            selectSection('tables');
            return false;
          }
          tableSettings.value?.discard();
          discard();
          return true;
        }
        if (disposed) return false;
        if (!dirty.value) return true;
        const confirmed = await modals.confirm({
          title: t('floorPlan.leave.title'),
          message: t('reservations.settings.unsaved'),
          acceptText: t('reservations.settings.discard'),
          cancelText: t('cancel'),
          acceptColor: 'error',
        });
        if (disposed || !confirmed) return false;
        discard();
        return (await tableSettings.value?.flush()) ?? true;
      })().finally(() => {
        leaveConfirmation = null;
      });
    return leaveConfirmation;
  }
  // This is a tab, not a routed component: beforeRouteLeave would never run.
  const removeBranchGuard = registerReservationBranchLeave(confirmLeave);
  const removeRouteGuard = proxy.$router.beforeEach((to, from, next) => {
    if (to.fullPath === from.fullPath) {
      next();
      return;
    }
    void confirmLeave()
      .then((confirmed) => (confirmed ? next() : next(false)))
      .catch(() => next(false));
  });
  useEventListener(window, 'beforeunload', (event: BeforeUnloadEvent) => {
    if (!dirty.value && !tableSettings.value?.hasPending()) return;
    event.preventDefault();
    event.returnValue = '';
  });
  onScopeDispose(() => {
    disposed = true;
    removeRouteGuard();
    removeBranchGuard();
  });
  defineExpose({ confirmLeave });
</script>

<template>
  <div ref="rootElement" class="reservations-settings">
    <div ref="scrollElement" class="settings-scroll">
      <div
        v-if="!configuration && status === 'loading'"
        class="loading-settings"
        aria-busy="true"
      >
        <PosUiSkeleton class="skeleton-title" />
        <PosUiSkeleton
          v-for="index in 4"
          :key="index"
          class="skeleton-section"
        />
      </div>
      <PosUiEmptyState
        v-else-if="!configuration && status === 'error'"
        icon="alert-circle-outline"
        :title="$t('reservations.settings.loadError')"
      >
        <template #action
          ><PosUiButton
            color="neutral"
            variant="outline"
            icon="refresh"
            @click="reload"
            >{{ $t('reservations.page.retry') }}</PosUiButton
          ></template
        >
      </PosUiEmptyState>
      <div v-else-if="configuration && draft" class="settings-layout">
        <nav
          ref="navElement"
          class="section-navigation"
          :aria-label="$t('reservations.tabs.settings')"
        >
          <div ref="linksElement" class="section-links">
            <button
              v-for="section in sections"
              :key="section"
              type="button"
              class="section-link"
              :class="{ active: activeSection === section }"
              :aria-current="activeSection === section ? 'location' : undefined"
              @click="selectSection(section)"
            >
              {{ $t(`reservations.settings.nav.${section}`) }}
            </button>
            <!-- Not a section: opens the step-by-step guide, so no scroll-spy. -->
            <button
              type="button"
              class="section-link guide-link"
              @click="emit('setup')"
            >
              {{ $t('reservations.settings.nav.guide') }}
            </button>
          </div>
        </nav>
        <div class="settings-sections">
          <div v-if="status === 'error'" class="reload-error" role="alert">
            <span>{{ $t('reservations.settings.loadError') }}</span>
            <PosUiButton
              color="neutral"
              variant="ghost"
              icon="refresh"
              @click="reload"
              >{{ $t('reservations.page.retry') }}</PosUiButton
            >
          </div>
          <V2ReservationsSetupChecklist
            v-if="!setupComplete"
            :configuration="configuration"
            @go="selectSection"
            @setup="emit('setup')"
          />
          <section
            id="rsv-settings-general"
            class="settings-section"
            :aria-label="$t('reservations.settings.nav.general')"
          >
            <V2ReservationsGeneralSettings
              v-model="draft"
              :whatsapp-available="configuration.settings.whatsappAvailable"
              :default-staff-email="auth.user?.email ?? null"
              :max-bookable-party="maxBookableParty"
              @go="selectSection"
            />
          </section>
          <section
            id="rsv-settings-shifts"
            class="settings-section"
            :aria-label="$t('reservations.settings.nav.shifts')"
          >
            <V2ReservationsShiftsSettings
              :configuration="configuration"
              :settings="draft"
              :settings-dirty="tableTimesDirty"
              @saved="reload"
            />
          </section>
          <section
            id="rsv-settings-dates"
            class="settings-section"
            :aria-label="$t('reservations.settings.nav.dates')"
          >
            <V2ReservationsDatesSettings
              :configuration="configuration"
              @saved="reload"
            />
          </section>
          <section
            id="rsv-settings-tables"
            class="settings-section"
            :aria-label="$t('reservations.settings.nav.tables')"
          >
            <V2ReservationsTablesPlanSettings
              ref="tableSettings"
              :configuration="configuration"
              :online-booking="draft.enabled && draft.onlineEnabled"
              :min-party-online="draft.minPartyOnline"
              :max-party-online="draft.maxPartyOnline"
              @saved="reload"
              @lower-max="lowerMaxParty"
            />
          </section>
          <section
            id="rsv-settings-deposit"
            class="settings-section"
            :aria-label="$t('reservations.settings.nav.deposit')"
          >
            <V2ReservationsDepositSettings
              v-model="draft"
              :available="configuration.settings.depositsAvailable"
              :mp-connected="configuration.settings.mpConnected"
              :currency="currency"
              :payments-url="paymentsUrl"
            />
          </section>
          <section
            id="rsv-settings-messages"
            class="settings-section"
            :aria-label="$t('reservations.settings.nav.messages')"
          >
            <V2ReservationsMessagesSettings v-model="draft" />
          </section>
          <section
            id="rsv-settings-link"
            class="settings-section"
            :aria-label="$t('reservations.settings.nav.link')"
          >
            <V2ReservationsLinkSettings :value="configuration.settings" />
          </section>
        </div>
      </div>
      <PosUiEmptyState
        v-else
        icon="store-outline"
        :title="$t('branch')"
        :description="$t('reservations.settings.noBranch')"
      />
    </div>
    <div v-show="dirty" class="save-bar" :aria-busy="saving">
      <p>
        <PosUiIcon name="circle-small" :size="24" />{{
          $t('reservations.settings.unsaved')
        }}
      </p>
      <div class="save-actions">
        <PosUiButton
          color="neutral"
          variant="outline"
          :disabled="saving"
          @click="discard"
          >{{ $t('reservations.settings.discard') }}</PosUiButton
        >
        <PosUiButton
          icon="content-save-outline"
          :aria-label="$t('reservations.settings.save')"
          :disabled="saving"
          @click="saveDraft"
          >{{ $t('reservations.settings.save') }}</PosUiButton
        >
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .reservations-settings {
    @apply tw-flex tw-h-full tw-min-h-0 tw-flex-col;
    .settings-scroll {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto;
      // The floor plan sizes its cells from this width: keep the gutter stable.
      scrollbar-gutter: stable;
    }
    .settings-layout {
      @apply tw-mx-auto tw-max-w-6xl lg:tw-grid lg:tw-grid-cols-[200px_minmax(0,1fr)] lg:tw-gap-6 lg:tw-px-6 lg:tw-pb-6;
    }
    // Below lg a white sticky chip row; on lg a plain list on the page canvas.
    .section-navigation {
      @apply tw-sticky tw-top-0 tw-z-10 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-bg-white lg:tw-mt-6 lg:tw-self-start lg:tw-border-b-0 lg:tw-bg-transparent;
      .section-links {
        @apply tw-flex tw-gap-1 tw-overflow-x-auto tw-px-4 tw-py-2 lg:tw-flex-col lg:tw-p-0;
        // Faded edges tell phone users the row scrolls sideways.
        mask-image: linear-gradient(
          to right,
          transparent,
          #000 1rem,
          #000 calc(100% - 1rem),
          transparent
        );
        -webkit-mask-image: linear-gradient(
          to right,
          transparent,
          #000 1rem,
          #000 calc(100% - 1rem),
          transparent
        );
        @screen lg {
          mask-image: none;
          -webkit-mask-image: none;
        }
      }
    }
    .section-link {
      @apply tw-flex tw-min-h-11 tw-shrink-0 tw-items-center tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-text-left tw-text-sm tw-font-medium tw-text-neutral-500 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-500/40;
      // Touch screens have no hover: a chip left under the finger must not
      // look like a second selection.
      @media (hover: hover) {
        &:hover {
          @apply tw-bg-neutral-100 lg:tw-bg-neutral-200;
        }
      }
      &.active {
        @apply tw-bg-orange-50 tw-text-orange-700;
      }
      // A quiet entry after the sections: hairline above it on the side list.
      &.guide-link {
        @apply lg:tw-mt-2 lg:tw-border-0 lg:tw-border-t lg:tw-border-solid lg:tw-border-neutral-200 lg:tw-pt-2 lg:tw-rounded-t-none;
      }
    }
    // The page itself is the canvas well: every section is a white card on it.
    .settings-sections {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-6 tw-px-4 tw-py-5 md:tw-px-6 lg:tw-mt-6 lg:tw-p-0;
    }
    .settings-section {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-6;
      scroll-margin-top: 72px;
    }
    .reload-error {
      @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-bg-red-50 tw-px-4 tw-py-2 tw-text-sm tw-text-red-700;
    }
    .loading-settings {
      @apply tw-mx-auto tw-max-w-3xl tw-p-6;
      .skeleton-title {
        @apply tw-mb-6 tw-h-6 tw-w-40;
      }
      .skeleton-section {
        @apply tw-mb-4 tw-h-28 tw-w-full;
      }
    }
    .save-bar {
      @apply tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-3 md:tw-px-6;
      p {
        @apply tw-m-0 tw-flex tw-items-center tw-text-xs tw-font-medium tw-text-neutral-700;
      }
    }
    .save-actions {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    @media (min-width: 1024px) {
      .settings-section {
        scroll-margin-top: 24px;
      }
    }
  }
</style>
